import { Fragment, ReactNode } from "react";

import { Button } from "../buttons";
import { Row } from "../containers";
import { Icon } from "../icons";
import { Text } from "../typography";
import { DataGridFilter } from "./types";
import { GroupedViewToggle } from "./GroupedViewToggle";

interface ToolbarProps<TRow> {
    filters?: DataGridFilter<TRow>[];
    activeFilterCount: number;
    showResetFilters?: boolean;
    onResetFilters?: () => void;
    toolbarActions?: ReactNode;
    /** When true, renders the grouped/flat view toggle on the right side of the
     *  toolbar. Pair with `flatView` + `onFlatViewChange`. */
    showViewToggle?: boolean;
    /** Current flat-view flag — drives the toggle's selected state. */
    flatView?: boolean;
    /** Called when the user flips the toggle. */
    onFlatViewChange?: (flat: boolean) => void;
}

/**
 * Strip above the table: filter chips, reset, `toolbarActions`, and optional
 * grouped/flat view toggle. Returns `null` when nothing has content — the table
 * renders flush with whatever's above it.
 */
export function Toolbar<TRow>({
    filters,
    activeFilterCount,
    showResetFilters,
    onResetFilters,
    toolbarActions,
    showViewToggle,
    flatView,
    onFlatViewChange
}: ToolbarProps<TRow>) {
    const hasFilters = !!filters?.length;
    const hasActions = !!toolbarActions;
    const hasViewToggle = !!showViewToggle && !!onFlatViewChange;

    if (!hasFilters && !hasActions && !hasViewToggle) return null;

    return (
        <Row spaceBetween sx={{ alignItems: "center", gap: 1, mb: 1.5 }}>
            <Row spacing={2} sx={{ alignItems: "center", flexWrap: "nowrap" }}>
                {hasFilters && (
                    <Row spacing={1} sx={{ alignItems: "center", flexWrap: "nowrap" }}>
                        {filters!.map((f, i) => (
                            <Fragment key={i}>{f.component}</Fragment>
                        ))}
                    </Row>
                )}
                {showResetFilters && (
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={onResetFilters}
                        textProps={{ sx: { fontWeight: "normal", gap: 0.5 } }}
                    >
                        <Icon type="reject" />
                        Reset all
                    </Button>
                )}
                {activeFilterCount > 0 && !showResetFilters && (
                    <Text color="caption" variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {activeFilterCount} active
                    </Text>
                )}
            </Row>
            <Row spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
                {toolbarActions}
                {hasViewToggle && (
                    <GroupedViewToggle isGrouped={!flatView} onChange={(isGrouped) => onFlatViewChange!(!isGrouped)} />
                )}
            </Row>
        </Row>
    );
}
